import { normalizeHostname } from './listing-host'

const EMBED_QUERY_PARAMETER = 'qpEmbed'
const EMBED_HOST_QUERY_PARAMETER = 'qpHost'

let cachedIsEmbedded: boolean | null = null
let cachedEmbedHostname: string | null | undefined = undefined

/**
 * Determines whether the QuickPass app is running as an embedded experience
 * (mounted inside a host site via the SDK overlay) rather than as the
 * standalone PWA / Capacitor shell.
 *
 * Two signals, OR'd together:
 *   1. An explicit `?qpEmbed=1` query parameter the SDK appends when it builds
 *      the overlay iframe URL — the intentional, forward-looking signal.
 *   2. A structural check that the app is running inside an iframe
 *      (`window.self !== window.top`) — covers older cached SDK builds that
 *      predate the flag, and any future same-frame embed.
 *
 * The standalone PWA and the Capacitor shell are always top-level documents, so
 * the iframe check never misfires for them. The hidden `/sdk-bridge` iframe
 * renders no navigation, so it does not matter that it also reads as embedded.
 * The result is computed once and cached for the page session.
 *
 * @returns {boolean} true when running inside a host-site embed
 */
const isEmbeddedExperience = (): boolean => {
  if (cachedIsEmbedded !== null) {
    return cachedIsEmbedded
  }

  if (typeof window === 'undefined') {
    cachedIsEmbedded = false
    return cachedIsEmbedded
  }

  let hasEmbedFlag = false
  try {
    const parameters = new URLSearchParams(window.location.search)
    hasEmbedFlag = parameters.get(EMBED_QUERY_PARAMETER) === '1'
  } catch {
    hasEmbedFlag = false
  }

  let isInsideIframe = false
  try {
    isInsideIframe = window.self !== window.top
  } catch {
    isInsideIframe = true
  }

  cachedIsEmbedded = hasEmbedFlag === true || isInsideIframe === true
  return cachedIsEmbedded
}

/**
 * Returns the normalised hostname of the host site the SDK overlay is embedded
 * on (e.g. `rentals.ca`), read from the `?qpHost=` parameter the SDK appends to
 * the overlay iframe URL. Lower-cased and stripped of a leading `www.`. Returns
 * null when running standalone, when not embedded, or when no host was supplied
 * (e.g. an older SDK build). Cached for the page session.
 *
 * @returns {string | null} the host site hostname, or null
 */
const getEmbedHostname = (): string | null => {
  if (cachedEmbedHostname !== undefined) {
    return cachedEmbedHostname
  }

  if (typeof window === 'undefined' || isEmbeddedExperience() === false) {
    cachedEmbedHostname = null
    return cachedEmbedHostname
  }

  let rawHost = ''
  try {
    const parameters = new URLSearchParams(window.location.search)
    rawHost = parameters.get(EMBED_HOST_QUERY_PARAMETER) ?? ''
  } catch {
    rawHost = ''
  }

  const normalized = normalizeHostname(rawHost)
  cachedEmbedHostname = normalized === '' ? null : normalized
  return cachedEmbedHostname
}

export { isEmbeddedExperience, getEmbedHostname }
